import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Languages,
  BookmarkCheck,
  Settings as SettingsIcon,
  Shield,
  LogOut,
} from 'lucide-react';
import { useMe, useServerVersion } from '@/api/hooks';
import { apiFetch } from '@/api/client';
import { useAuthStore } from '@/store/auth';
import { Avatar } from '@/components/Avatar';
import { Tooltip } from '@/components/Tooltip';
import { UpdateAvailableHint } from '@/components/UpdateAvailableHint';
import clsx from 'clsx';

function navClass({ isActive }: { isActive: boolean }) {
  return clsx(
    'flex flex-1 flex-col items-center gap-0.5 px-2 py-2 text-xs font-medium transition',
    isActive ? 'text-brand-600' : 'text-slate-500 hover:text-slate-700',
  );
}

export function Layout() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const sv = useServerVersion(me.data?.settings.updateCheckInterval);
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clear);

  const serverVersion = sv.data?.version;
  const hasUpdate = serverVersion != null && serverVersion !== __APP_VERSION__;

  const uiLocale = me.data?.settings.uiLocale ?? 'en';
  if (i18n.resolvedLanguage !== uiLocale) {
    void i18n.changeLanguage(uiLocale);
  }

  const logout = async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch {
      // best-effort
    }
    clearAuth();
    navigate('/login', { replace: true });
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-2xl flex-col">
      <header className="flex items-center justify-between gap-2 px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
          <div className="flex items-center gap-2 text-slate-800">
            <Languages className="h-5 w-5 text-brand-600" />
            <span className="font-semibold">{t('app.name')}</span>
          </div>
          <span className="inline-flex items-center gap-1 text-xs text-slate-400">
            v{__APP_VERSION__}
            {hasUpdate && serverVersion && (
              <UpdateAvailableHint serverVersion={serverVersion} />
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {me.data && (
            <Tooltip label={me.data.name || me.data.email}>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <Avatar name={me.data.name} avatarUrl={me.data.avatarUrl} size={20} />
              </button>
            </Tooltip>
          )}
          <button onClick={logout} className="btn-ghost" aria-label={t('nav.logout')}>
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>
      <main className="flex-1 px-4 pb-24">
        <Outlet />
      </main>
      <nav className="fixed inset-x-0 bottom-0 z-10 border-t border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-2xl">
          <NavLink to="/" end className={navClass}>
            <Languages className="h-5 w-5" />
            {t('nav.translate')}
          </NavLink>
          <NavLink to="/lists" className={navClass}>
            <BookmarkCheck className="h-5 w-5" />
            {t('nav.lists')}
          </NavLink>
          <NavLink to="/settings" className={navClass}>
            <SettingsIcon className="h-5 w-5" />
            {t('nav.settings')}
          </NavLink>
          {me.data?.role === 'ADMIN' && (
            <NavLink to="/admin" className={navClass}>
              <Shield className="h-5 w-5" />
              {t('nav.admin')}
            </NavLink>
          )}
        </div>
      </nav>
    </div>
  );
}
